import React, { useState, useEffect } from 'react';
import { Wallet, Calculator, Loader2 } from 'lucide-react';
import MyRewardsTab from './MyRewardsTab';
import EarningsCalculatorTab from './EarningsCalculatorTab';
import rewardService from '../../../services/rewardService';

export default function RewardsPanel({ onClose }) {
  const [activeTab, setActiveTab] = useState('rewards');
  const [loading, setLoading] = useState(true);
  const [balanceData, setBalanceData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [tokenPrice, setTokenPrice] = useState(0.40);
  const [closestMilestone, setClosestMilestone] = useState(null);
  const [weeklyStreakThreshold, setWeeklyStreakThreshold] = useState(5);
  const [weeklyStreakBonus, setWeeklyStreakBonus] = useState(0.5);
  const [minWithdrawalUSD, setMinWithdrawalUSD] = useState(100);

  useEffect(() => {
    fetchRewardsData();
  }, []);

  const fetchRewardsData = async () => {
    try {
      setLoading(true);
      const [balanceRes, txRes] = await Promise.all([
        rewardService.getBalance(),
        rewardService.getTransactions(20),
      ]);

      if (balanceRes.success) {
        setBalanceData(balanceRes.balance);
        setTokenPrice(balanceRes.tokenPrice || 0.40);
        setClosestMilestone(balanceRes.closestMilestone);
        setWeeklyStreakThreshold(balanceRes.weeklyStreakThreshold || 5);
        setWeeklyStreakBonus(balanceRes.weeklyStreakBonus || 0.5);
        setMinWithdrawalUSD(balanceRes.minWithdrawalUSD || 100);
      }

      if (txRes.success) {
        setTransactions(txRes.transactions || []);
      }
    } catch (error) {
      console.error('[RewardsPanel] Failed to fetch rewards data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#0a0a1a] via-[#1a1a2e] to-[#0a0a1a]">
      {/* Tab Bar */}
      <div className="w-full bg-white/5 p-1 border-b border-white/10 flex">
        <button
          onClick={() => setActiveTab('rewards')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'rewards'
              ? 'bg-[#8b5cf6] text-white'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          <Wallet className="w-4 h-4" />
          My Rewards
        </button>
        <button
          onClick={() => setActiveTab('calculator')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'calculator'
              ? 'bg-[#8b5cf6] text-white'
              : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          <Calculator className="w-4 h-4" />
          Calculator
        </button>
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-[#8b5cf6] animate-spin" />
        </div>
      ) : (
        <>
          {activeTab === 'rewards' && (
            <MyRewardsTab
              balance={balanceData}
              transactions={transactions}
              tokenPrice={tokenPrice}
              closestMilestone={closestMilestone}
              weeklyStreakThreshold={weeklyStreakThreshold}
              weeklyStreakBonus={weeklyStreakBonus}
              minWithdrawalUSD={minWithdrawalUSD}
            />
          )}
          {activeTab === 'calculator' && (
            <EarningsCalculatorTab balance={balanceData} tokenPrice={tokenPrice} />
          )}
        </>
      )}
    </div>
  );
}
